import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';

const MIN_PASSWORD_LENGTH = 8;
const GENERATED_PASSWORD_LENGTH = 16;
/** Omits 0/O, 1/l/I so a generated password is readable when copied onto paper. */
const PASSWORD_ALPHABET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*?';

function generateTemporaryPassword(): string {
  const bytes = new Uint8Array(GENERATED_PASSWORD_LENGTH);
  crypto.getRandomValues(bytes);
  let result = '';
  for (let i = 0; i < bytes.length; i += 1) {
    const byte = bytes[i];
    if (byte === undefined) continue;
    const index = byte % PASSWORD_ALPHABET.length;
    result += PASSWORD_ALPHABET[index] ?? '';
  }
  return result;
}

export interface ResetMemberPasswordDialogProps {
  open: boolean;
  member: { id: string; name: string };
  onClose: () => void;
}

export function ResetMemberPasswordDialog({
  open,
  member,
  onClose,
}: ResetMemberPasswordDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setShowPassword(false);
    setSubmitting(false);
  }, [open]);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const isValid = password.length >= MIN_PASSWORD_LENGTH;
  const confirmDisabled = !isValid || submitting;

  const handleGenerate = () => {
    setPassword(generateTemporaryPassword());
    setShowPassword(true);
  };

  const handleCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      notify.success(t('resetMemberPassword.copied'));
    } catch {
      notify.error(tc('copyFailed'));
    }
  };

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/users/${member.id}/reset-password`, { newPassword: password });
      notify.success(t('resetMemberPassword.success'));
      onClose();
    } catch {
      notify.error(t('resetMemberPassword.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const visibilityLabel = showPassword
    ? t('resetMemberPassword.hidePassword')
    : t('resetMemberPassword.showPassword');

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={t('resetMemberPassword.action')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('resetMemberPassword.action')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">{member.name}</Typography>
          <TextField
            label={t('resetMemberPassword.temporaryPassword')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => { setPassword(event.target.value); }}
            autoFocus
            fullWidth
            autoComplete="new-password"
            error={tooShort}
            helperText={
              tooShort
                ? t('resetMemberPassword.minLength', { count: MIN_PASSWORD_LENGTH })
                : t('resetMemberPassword.helper')
            }
            disabled={submitting}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={visibilityLabel}>
                      <IconButton
                        onClick={() => { setShowPassword((visible) => !visible); }}
                        aria-label={visibilityLabel}
                        edge="end"
                        disabled={submitting}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('resetMemberPassword.copy')}>
                      <span>
                        <IconButton
                          onClick={() => { void handleCopy(); }}
                          aria-label={t('resetMemberPassword.copy')}
                          edge="end"
                          disabled={!password || submitting}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="outlined"
            onClick={handleGenerate}
            disabled={submitting}
          >
            {t('resetMemberPassword.generate')}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => { void handleSubmit(); }}
          disabled={confirmDisabled}
        >
          {submitting ? <CircularProgress size={20} /> : t('resetMemberPassword.action')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
